import { useState, type CSSProperties, type ReactNode } from "react";
import { evidenceQualityBadge, formatVerificationAge } from "../verification";
import {
  operatorFacingMessage,
  shortOperatorRecommendation,
} from "./operatorCopy";

/**
 * Presentational browser for persisted VerificationResult history.
 * Both Ops Dashboard and CP Admin fetch via GET /api/verification/* and pass items here.
 * Does not invent status — every row is a stored VerificationResult (or history item).
 * Default: collapsed behind "Show history" (progressive disclosure).
 */

export type VerificationHistoryItemView = {
  tenant_slug?: string;
  component: string;
  category?: string;
  status: string;
  verification_method?: string;
  timestamp?: string | null;
  duration_ms?: number | null;
  endpoint?: string | null;
  evidence?: { label: string; value: string }[];
  recommendation?: string | null;
  confidence?: string | null;
  freshness?: string | null;
  deployment_id?: string | null;
  incident_id?: string | null;
  request_id?: string | null;
  trace_id?: string | null;
};

export type VerificationHistoryPanelProps = {
  title?: string;
  subtitle?: string;
  items: VerificationHistoryItemView[];
  loading?: boolean;
  error?: string | null;
  emptyMessage?: string;
  /** Optional refresh control for the operator. */
  onRefresh?: () => void;
  /** When true (default), history list stays collapsed until the operator expands it. */
  collapseByDefault?: boolean;
  className?: string;
  style?: CSSProperties;
};

function statusColor(status: string): string {
  switch (status) {
    case "verified":
      return "#4ade80";
    case "warning":
      return "#fbbf24";
    case "failed":
      return "#f87171";
    default:
      return "#94a3b8";
  }
}

function formatWhen(raw: string | null | undefined): string {
  if (!raw) return "Unknown time";
  try {
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return raw;
    return d.toLocaleString();
  } catch {
    return raw;
  }
}

export function VerificationHistoryPanel({
  title = "Verification history",
  subtitle = "Persisted VerificationResult records from the Verification Event Store.",
  items,
  loading = false,
  error = null,
  emptyMessage = "No verification history for this scope yet.",
  onRefresh,
  collapseByDefault = true,
  className = "",
  style,
}: VerificationHistoryPanelProps): ReactNode {
  const [showHistory, setShowHistory] = useState(!collapseByDefault);
  const latest = items[0];

  return (
    <div
      className={`verification-history-panel ${className}`.trim()}
      style={style}
      data-count={items.length}
      data-collapsed={showHistory ? "false" : "true"}
    >
      <header style={{ marginBottom: showHistory ? "0.75rem" : 0 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "0.75rem",
          }}
        >
          <p
            style={{
              margin: 0,
              fontSize: "0.7rem",
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: "#94a3b8",
            }}
          >
            {title}
          </p>
          <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
            {onRefresh && showHistory ? (
              <button
                type="button"
                onClick={onRefresh}
                disabled={loading}
                style={{
                  padding: "0.2rem 0.55rem",
                  border: "1px solid #334155",
                  borderRadius: "0.3rem",
                  background: "transparent",
                  color: "#93c5fd",
                  fontSize: "0.68rem",
                  cursor: loading ? "wait" : "pointer",
                }}
              >
                {loading ? "Loading…" : "Refresh"}
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => {
                setShowHistory((v) => !v);
                if (!showHistory && onRefresh && items.length === 0) onRefresh();
              }}
              style={{
                padding: "0.2rem 0.55rem",
                border: "1px solid #334155",
                borderRadius: "0.3rem",
                background: "transparent",
                color: "#93c5fd",
                fontSize: "0.68rem",
                cursor: "pointer",
              }}
            >
              {showHistory
                ? "Hide history"
                : items.length > 0
                  ? `▶ Show history (${items.length})`
                  : "▶ Show history"}
            </button>
          </div>
        </div>
        {!showHistory ? (
          <p style={{ margin: "0.35rem 0 0", fontSize: "0.78rem", color: "#cbd5e1" }}>
            {loading && items.length === 0
              ? "Loading…"
              : error
                ? error
                : latest
                  ? `${latest.status} · ${latest.component} · ${formatWhen(latest.timestamp)}`
                  : emptyMessage}
          </p>
        ) : (
          <p style={{ margin: "0.3rem 0 0", fontSize: "0.78rem", color: "#cbd5e1" }}>{subtitle}</p>
        )}
      </header>

      {showHistory ? (
        <>
          {error ? (
            <p style={{ margin: 0, fontSize: "0.8rem", color: "#f87171" }}>{error}</p>
          ) : null}

          {loading && items.length === 0 ? (
            <p style={{ margin: 0, fontSize: "0.8rem", color: "#64748b" }}>
              Loading verification history…
            </p>
          ) : null}

          {!loading && !error && items.length === 0 ? (
            <p style={{ margin: 0, fontSize: "0.8rem", color: "#64748b" }}>{emptyMessage}</p>
          ) : null}

          {items.length > 0 ? (
            <ol style={{ margin: 0, padding: 0, listStyle: "none" }}>
              {items.map((item, index) => {
                const key = `${item.component}-${item.timestamp ?? index}-${item.status}-${index}`;
                const correlations = [
                  item.deployment_id ? `deployment ${item.deployment_id}` : null,
                  item.incident_id ? `incident ${item.incident_id}` : null,
                  item.request_id ? `request ${item.request_id}` : null,
                  item.trace_id ? `trace ${item.trace_id}` : null,
                ].filter(Boolean);
                return (
                  <li
                    key={key}
                    style={{
                      marginTop: index === 0 ? 0 : "0.65rem",
                      paddingTop: index === 0 ? 0 : "0.65rem",
                      borderTop: index === 0 ? "none" : "1px solid #1e293b",
                    }}
                  >
                    <p style={{ margin: 0, fontSize: "0.8rem", color: "#f1f5f9" }}>
                      <span style={{ color: statusColor(item.status), fontWeight: 600 }}>
                        {item.status}
                      </span>
                      {" · "}
                      <strong>{item.component}</strong>
                      {item.category ? (
                        <span style={{ color: "#64748b" }}>{` · ${item.category}`}</span>
                      ) : null}
                    </p>
                    <p style={{ margin: "0.2rem 0 0", fontSize: "0.68rem", color: "#94a3b8" }}>
                      {(() => {
                        const age = formatVerificationAge(item.timestamp);
                        return age ? `Verified ${age}` : formatWhen(item.timestamp);
                      })()}
                      {item.verification_method ? ` · ${item.verification_method}` : ""}
                      {item.duration_ms != null ? ` · ${item.duration_ms} ms` : ""}
                      {item.confidence ? ` · confidence ${item.confidence}` : ""}
                      {item.freshness ? ` · ${evidenceQualityBadge(item.freshness)}` : ""}
                    </p>
                    <details style={{ marginTop: "0.25rem" }}>
                      <summary
                        style={{
                          cursor: "pointer",
                          fontSize: "0.68rem",
                          color: "#93c5fd",
                          listStyle: "none",
                        }}
                      >
                        ▶ Diagnostics
                      </summary>
                      <div style={{ marginTop: "0.25rem" }}>
                        {item.endpoint ? (
                          <p style={{ margin: "0.15rem 0 0", fontSize: "0.68rem", color: "#64748b" }}>
                            Endpoint: {item.endpoint}
                          </p>
                        ) : null}
                        {item.tenant_slug ? (
                          <p style={{ margin: "0.15rem 0 0", fontSize: "0.68rem", color: "#64748b" }}>
                            Tenant: {item.tenant_slug}
                          </p>
                        ) : null}
                        {correlations.length > 0 ? (
                          <p style={{ margin: "0.15rem 0 0", fontSize: "0.68rem", color: "#64748b" }}>
                            Correlated: {correlations.join(" · ")}
                          </p>
                        ) : null}
                        {item.evidence && item.evidence.length > 0 ? (
                          <ul
                            style={{
                              margin: "0.3rem 0 0",
                              paddingLeft: "1rem",
                              fontSize: "0.68rem",
                              color: "#cbd5e1",
                            }}
                          >
                            {item.evidence.map((e) => (
                              <li key={`${e.label}-${e.value}`}>
                                <span style={{ color: "#94a3b8" }}>{e.label}: </span>
                                {operatorFacingMessage(e.value) ?? e.value}
                              </li>
                            ))}
                          </ul>
                        ) : null}
                        {item.recommendation ? (
                          <p style={{ margin: "0.25rem 0 0", fontSize: "0.7rem", color: "#fcd34d" }}>
                            Recommendation:{" "}
                            {shortOperatorRecommendation(item.recommendation) ??
                              item.recommendation}
                          </p>
                        ) : null}
                      </div>
                    </details>
                  </li>
                );
              })}
            </ol>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
